import { Link } from '@tanstack/react-router'
import { CheckCircle2, ShieldCheck, UserCheck } from 'lucide-react'
import type { ReactNode } from 'react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

/**
 * Saying, before the click, that a ledger action is countersigned — and saying afterwards what
 * actually happened.
 *
 * Four ledger commands work this way: a manual journal, a reversal, a period reopening, and the
 * business day close. Each answers with an envelope carrying `awaitingApproval` rather than with
 * the thing that was asked for, and when it is true the command is **stored and nothing has
 * happened**.
 *
 * That distinction is the difference between a ledger that can be trusted and one that cannot. A
 * maker who reads "posted" against a manual journal that is sitting in an approval queue will go
 * and reconcile against an entry that does not exist; a branch manager who reads "closed" against a
 * queued day close will report figures for a day that is still trading. Neither person finds out
 * until somebody else does, later, from the other side.
 *
 * So no ledger screen reports a countersigned command's result any other way, and nothing infers
 * success from the absence of an error.
 */

/**
 * The minimum a countersigned ledger response carries.
 *
 * Every one of the four envelopes is assignable to this — `PostedJournalResult`,
 * `LedgerPostingPeriodResult` and `ClosedBusinessDay` all carry these two members and more.
 */
export interface LedgerApprovalEnvelope {
  awaitingApproval: boolean
  approvalRequestId: string | null
}

export interface LedgerDualControlNoticeProps {
  /** The action as a noun phrase: "Raising a journal by hand". */
  action: string
  /** The countersigning code, named so an operator can ask for it by name. */
  approvePermission: string
  /** Whether this user holds it — in which case their own request may complete on the click. */
  canCountersign: boolean
  /** How long the request survives unanswered, where the policy is shorter than the default. */
  expiry?: string
  /** Anything specific to this command that has to be read before the click. */
  children?: ReactNode
}

/**
 * What confirming will actually do, said before the operator commits to it.
 *
 * The wording turns on whether this user holds the countersigning code, and it is careful in both
 * directions. Holding it does not *guarantee* immediate completion — an institution's approval
 * policy is a configurable row and may still call for a second person — so the notice says "may"
 * rather than "will". Not holding it does guarantee a queue.
 */
export function LedgerDualControlNotice({
  action,
  approvePermission,
  canCountersign,
  expiry,
  children,
}: LedgerDualControlNoticeProps) {
  return (
    <Alert tone={canCountersign ? 'info' : 'warning'}>
      {canCountersign ? <ShieldCheck aria-hidden /> : <UserCheck aria-hidden />}
      <div className="space-y-1">
        <AlertTitle>{action} is countersigned</AlertTitle>
        <AlertDescription>
          {canCountersign ? (
            <>
              Your role holds <span className="font-mono text-xs">{approvePermission}</span>, so this
              may complete as soon as you confirm it. If the institution&apos;s approval policy still
              calls for a second person it is queued instead — and the answer on this screen will say
              which of the two happened rather than assuming.
            </>
          ) : (
            <>
              Your role does not hold <span className="font-mono text-xs">{approvePermission}</span>,
              so confirming this <strong>stores the request and changes nothing in the ledger</strong>.
              Somebody holding that permission has to countersign it before anything posts.
            </>
          )}
          {expiry ? <> {expiry}</> : null}
        </AlertDescription>
        {children}
      </div>
    </Alert>
  )
}

export interface LedgerDualControlOutcomeProps {
  result: LedgerApprovalEnvelope
  /** What is now waiting, in one line. Written from the maker's point of view. */
  awaitingDescription: ReactNode
  /** What has now changed, in one line. */
  appliedDescription: ReactNode
  /** Detail that belongs under either outcome — an entry number to quote, a figure to re-read. */
  children?: ReactNode
}

/**
 * What the server actually did.
 *
 * `awaitingApproval` is the whole of it. There is no third state and nothing is inferred from the
 * absence of an error: a command that answered without it executed, and one that answered with it
 * did not. The queued panel leads with "nothing has posted" rather than burying it, and offers the
 * approvals queue, because the maker's next question is who has to sign.
 */
export function LedgerDualControlOutcome({
  result,
  awaitingDescription,
  appliedDescription,
  children,
}: LedgerDualControlOutcomeProps) {
  if (result.awaitingApproval) {
    return (
      <Alert tone="warning">
        <UserCheck aria-hidden />
        <div className="space-y-2">
          <AlertTitle>Sent for approval — nothing has posted</AlertTitle>
          <AlertDescription>
            {awaitingDescription}
            {result.approvalRequestId ? (
              <>
                {' '}
                Request <span className="font-mono text-xs">{result.approvalRequestId}</span>.
              </>
            ) : null}
          </AlertDescription>
          {children}
          <Button asChild variant="secondary" size="sm">
            <Link to="/approvals" search={{ tab: 'mine' }}>
              Track it under Submitted by me
            </Link>
          </Button>
        </div>
      </Alert>
    )
  }

  return (
    <Alert tone="success">
      <CheckCircle2 aria-hidden />
      <div className="space-y-1">
        <AlertTitle>Done</AlertTitle>
        <AlertDescription>{appliedDescription}</AlertDescription>
        {children}
      </div>
    </Alert>
  )
}
